/**
 * Vorlage „Zutat hinzugeben“.
 *
 * Ein Ei wird am Schüsselrand aufgeschlagen und gleitet aus der Schale, oder
 * ein Schälchen mit Butterstücken, Zucker oder geschnittenen Zutaten kippt
 * über dem Gefäß. Was hineinfällt, bleibt auf der Masse liegen.
 */
import { ease, lerp, rotatePoint, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipsePath, path } from '../paths.ts';
import { circle, compact, ellipse, faded, group, line, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';
import { falling } from '../parts/effects.ts';
import { cube } from '../parts/food.ts';
import { arrow } from '../parts/stage.ts';
import { TARGET_VESSELS, targetFromText, targetScene, targetVessel } from '../parts/target.ts';
import type { TargetVessel } from '../parts/target.ts';
import { bowl, contents, interiorClip } from '../parts/vessels.ts';
import type { Vessel } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Zutaten. */
const ITEMS = {
  egg: 'Ei',
  butter: 'Butter',
  sugar: 'Zucker',
  pieces: 'Stücke aus dem Schälchen',
  board: 'Geschnittenes vom Brett',
} as const;
type Item = keyof typeof ITEMS;

/** Füllstand der Masse im Gefäß. */
const LEVEL = 0.28;
/** Gelandetes liegt auf der hinteren Hälfte der Oberfläche, sonst verdeckt es der Rand. */
const BACK = -6;

/** Zeitplan des Eis. */
const EGG = {
  appear: [0.02, 0.08],
  toRim: [0.08, 0.16],
  tap: [0.16, 0.26],
  toCentre: [0.26, 0.34],
  open: [0.34, 0.42],
  fall: [0.38, 0.5],
  shellsOut: [0.5, 0.58],
  reset: [0.88, 1],
} as const;

/** Zeitplan des Schälchens. */
const DISH = {
  appear: [0.02, 0.12],
  tilt: [0.12, 0.24],
  release: [0.2, 0.4],
  untilt: [0.46, 0.54],
  vanish: [0.54, 0.62],
  reset: [0.88, 1],
} as const;

/** Ein Ei liegt quer: Länge 28, Höhe 21. */
const EGG_RX = 14;
const EGG_RY = 10.5;

/** Bruchkante quer durch das Ei, von oben nach unten. */
const CRACK: readonly Point[] = [
  { x: 0, y: -EGG_RY },
  { x: 2.5, y: -6 },
  { x: -2, y: -2.5 },
  { x: 2.5, y: 1 },
  { x: -2, y: 4.5 },
  { x: 2, y: 8 },
  { x: 0, y: EGG_RY },
];

/** Eine Schalenhälfte; side -1 links, 1 rechts. */
function shellHalf(side: -1 | 1): string {
  const builder = path();
  CRACK.forEach((point, index) =>
    index === 0 ? builder.move(point.x, point.y) : builder.line(point.x, point.y),
  );
  // Von unten über die Außenseite zurück nach oben.
  return builder
    .arc(EGG_RX, EGG_RY, false, side < 0, 0, -EGG_RY)
    .close()
    .toString();
}

/** Ausgelaufenes Ei: Eiweiß mit Dotter. */
function eggContents(cx: number, cy: number, spread: number): SceneNode[] {
  return [
    ellipse(cx, cy, lerp(7, 17, spread), lerp(6, 5, spread), styles.snow()),
    ellipse(cx + 1, cy - lerp(2, 1, spread), 5, lerp(4.5, 3.4, spread), {
      fill: 'shade',
      stroke: 'ink',
      strokeWidth: STROKE.detail,
    }),
  ];
}

/** Ei aufschlagen und hineingleiten lassen. */
function eggScene(vessel: Vessel, surfaceY: number, t: number): { inside: MaybeNode[]; above: MaybeNode[] } {
  const rimRight = { x: vessel.cx + vessel.rx - 10, y: vessel.rimY - EGG_RY + 1 };
  const centre = { x: vessel.cx + 8, y: vessel.rimY - 36 };
  const start = { x: vessel.cx + 60, y: 26 };
  const reset = ease.inOutSine(segment(t, ...EGG.reset));

  // Weg des Eis: herein, an den Rand, zweimal klopfen, über die Mitte.
  const toRim = ease.inOutCubic(segment(t, ...EGG.toRim));
  const toCentre = ease.inOutCubic(segment(t, ...EGG.toCentre));
  const tapPhase = segment(t, ...EGG.tap);
  const tap = tapPhase > 0 && tapPhase < 1 ? Math.abs(Math.sin(tapPhase * Math.PI * 2)) * 7 : 0;
  const x = lerp(lerp(start.x, rimRight.x, toRim), centre.x, toCentre);
  const y = lerp(lerp(start.y, rimRight.y - 7, toRim), centre.y, toCentre) + tap;
  const appear = ease.outCubic(segment(t, ...EGG.appear));
  const cracked = t >= EGG.tap[0] + 0.03;

  const open = ease.inOutCubic(segment(t, ...EGG.open));
  const shells = appear * (1 - segment(t, ...EGG.shellsOut));
  const lift = ease.inOutSine(segment(t, ...EGG.shellsOut)) * 10;
  const half = (side: -1 | 1): SceneNode =>
    group([shape(shellHalf(side), styles.solid('paper'))], {
      transform: { x: x + side * open * 7, y: y - lift, rotate: -side * open * 60, ox: 0, oy: 0 },
    });
  const whole = group(
    [
      shape(ellipsePath(0, 0, EGG_RX, EGG_RY), styles.solid('paper')),
      shape(ellipsePath(-5, -4.5, 4, 2), styles.flat('tint')),
    ],
    { transform: { x, y } },
  );

  // Inhalt fällt aus der Schale und breitet sich auf der Masse aus.
  const fall = segment(t, ...EGG.fall);
  const falling = fall > 0 && fall < 1;
  const dropY = lerp(centre.y + 4, surfaceY + BACK, ease.inQuad(fall));
  const landed = fall >= 1 ? 1 - reset : 0;

  return {
    inside: [
      falling ? group(eggContents(centre.x, dropY, 0)) : null,
      landed > 0 ? faded(landed, eggContents(centre.x, surfaceY + BACK, 1)) : null,
    ],
    above: [faded(shells, cracked ? [half(-1), half(1)] : [whole])],
  };
}

/** Darstellung der Zutaten im Schälchen. */
const PIECES: Readonly<Record<Exclude<Item, 'egg'>, { tone: Tone; size: number }>> = {
  butter: { tone: 'tint', size: 8 },
  sugar: { tone: 'snow', size: 0 },
  pieces: { tone: 'deep', size: 6 },
  board: { tone: 'tint', size: 9 },
};

/** Neigung des Schälchens beim Ausleeren. */
const FULL_TILT = -100;
/** Neigung des Bretts: Das Ende mit dem Griff hebt sich. */
const BOARD_TILT = -32;
/** Länge des Bretts. */
const BOARD_LENGTH = 74;

/** Geschnittenes auf dem Brett (eigene Koordinaten, vordere Kante im Ursprung). */
const ON_BOARD: readonly (Point & { turn: number })[] = [
  { x: 14, y: -5, turn: 8 },
  { x: 26, y: -5, turn: -12 },
  { x: 38, y: -5, turn: 20 },
  { x: 50, y: -5, turn: -6 },
  { x: 62, y: -5, turn: 14 },
];

/** Dort landen die Stücke vom Brett. */
const BOARD_LANDING: readonly Point[] = [
  { x: -20, y: 0 },
  { x: -4, y: -3 },
  { x: 12, y: 1 },
  { x: 26, y: -2 },
  { x: 4, y: 3 },
];

/** Schneidebrett von der Seite: Oberkante auf y = 0, Griff am hinteren Ende. */
function board(): SceneNode[] {
  return [
    shape(
      path()
        .move(0, 0)
        .line(BOARD_LENGTH, 0)
        .line(BOARD_LENGTH + 12, 1)
        .quad(BOARD_LENGTH + 20, 6, BOARD_LENGTH + 12, 11)
        .line(BOARD_LENGTH, 11)
        .line(3, 11)
        .quad(0, 11, 0, 8)
        .close()
        .toString(),
      styles.solid('tint'),
    ),
    circle(BOARD_LENGTH + 10, 5.5, 2, styles.flat('ink')),
    line(5, 5.5, BOARD_LENGTH - 5, 5.5, styles.fine('shade')),
  ];
}

/** Lage der Stücke im Schälchen (eigene Koordinaten, Kipppunkt im Ursprung). */
const IN_DISH: readonly (Point & { turn: number })[] = [
  { x: 10, y: 1, turn: 10 },
  { x: 19, y: -2, turn: -14 },
  { x: 28, y: 1, turn: 6 },
  { x: 16, y: 4, turn: 24 },
];

/** Dort landen die Stücke auf der Masse, relativ zur Gefäßmitte. */
const LANDING: readonly Point[] = [
  { x: -14, y: -1 },
  { x: 4, y: 2 },
  { x: 20, y: -2 },
  { x: -2, y: -4 },
];

/** Schälchen kippen. */
function dishScene(
  item: Exclude<Item, 'egg'>,
  vessel: Vessel,
  surfaceY: number,
  t: number,
): { inside: MaybeNode[]; above: MaybeNode[] } {
  const look = PIECES[item];
  const onBoard = item === 'board';
  const fullTilt = onBoard ? BOARD_TILT : FULL_TILT;
  const pivot = onBoard
    ? { x: vessel.cx + 26, y: vessel.rimY - 24 }
    : { x: vessel.cx + 34, y: vessel.rimY - 22 };
  const reset = ease.inOutSine(segment(t, ...DISH.reset));
  const appear = ease.outCubic(segment(t, ...DISH.appear));
  const visible = appear * (1 - segment(t, ...DISH.vanish));
  const slide = (1 - appear) * 30 + ease.inCubic(segment(t, ...DISH.vanish)) * 30;
  const tilt =
    fullTilt * (ease.inOutCubic(segment(t, ...DISH.tilt)) - ease.inOutCubic(segment(t, ...DISH.untilt)));
  const at = onBoard
    ? { x: pivot.x + slide * 0.2, y: pivot.y - slide * 0.6 }
    : { x: pivot.x + slide, y: pivot.y - slide * 0.6 };
  const dish = bowl(20, 0, 40, 18);
  const release = segment(t, ...DISH.release);

  const inDish: SceneNode[] = [];
  const falls: SceneNode[] = [];
  const landed: SceneNode[] = [];
  if (item === 'sugar') {
    // Zucker: ein Häufchen im Schälchen, das beim Kippen herausrieselt.
    const left = 1 - release;
    if (left > 0.02) {
      inDish.push(
        shape(
          path()
            .move(6, 3)
            .quad(20, 3 - 9 * left, 34, 3)
            .close()
            .toString(),
          styles.snow(),
        ),
      );
    }
    const flowing = release > 0 && release < 1;
    if (flowing) {
      falls.push(
        ...falling(at.x - 2, at.y + 4, surfaceY, 10, 16, t, { seed: 21, size: 1.4, loops: 4, tone: 'shade' }),
      );
    }
    const heap = Math.min(1, release * 1.2) * (1 - reset);
    if (heap > 0.02) {
      landed.push(
        shape(
          path()
            .move(pivot.x - 22, surfaceY + BACK + 3)
            .quad(pivot.x - 4, surfaceY + BACK + 3 - 14 * heap, pivot.x + 14, surfaceY + BACK + 3)
            .close()
            .toString(),
          styles.snow(),
        ),
      );
    }
  } else if (onBoard) {
    ON_BOARD.forEach((local, index) => {
      // Die Stücke rutschen nacheinander zur Kante und fallen hinein.
      const own = segment(release, index * 0.12, index * 0.12 + 0.5);
      const target = BOARD_LANDING[index] as Point;
      const end = { x: vessel.cx + target.x, y: surfaceY + BACK + target.y };
      if (own >= 1) {
        landed.push(cube({ ...end, turn: local.turn + 40 }, look.size, index % 2 === 0 ? 'tint' : 'deep'));
        return;
      }
      const slideOff = segment(own, 0, 0.45);
      const fall = segment(own, 0.45, 1);
      const tone = index % 2 === 0 ? 'tint' : 'deep';
      if (fall <= 0) {
        inDish.push(cube({ ...local, x: lerp(local.x, -2, ease.inQuad(slideOff)) }, look.size, tone));
        return;
      }
      const edge = rotatePoint({ x: -2, y: local.y }, { x: 0, y: 0 }, BOARD_TILT);
      const startWorld = { x: at.x + edge.x, y: at.y + edge.y };
      falls.push(
        cube(
          {
            x: lerp(startWorld.x, end.x, fall),
            y: lerp(startWorld.y, end.y, ease.inQuad(fall)),
            turn: local.turn + fall * 40,
          },
          look.size,
          tone,
        ),
      );
    });
  } else {
    IN_DISH.forEach((local, index) => {
      // Jedes Stück löst sich etwas später und fällt mit Schwerkraft.
      const own = segment(release, index * 0.18, index * 0.18 + 0.46);
      const position = { ...local };
      if (own <= 0) {
        inDish.push(cube(position, look.size, look.tone));
        return;
      }
      const target = LANDING[index] as Point;
      const end = { x: vessel.cx + target.x, y: surfaceY + BACK + target.y };
      if (own >= 1) {
        landed.push(cube({ ...end, turn: local.turn + 30 }, look.size, look.tone));
        return;
      }
      // Start dort, wo das Stück im voll gekippten Schälchen lag.
      const from = rotatePoint(local, { x: 0, y: 0 }, FULL_TILT);
      const startWorld = { x: at.x + from.x, y: at.y + from.y };
      falls.push(
        cube(
          {
            x: lerp(startWorld.x, end.x, own),
            y: lerp(startWorld.y, end.y, ease.inQuad(own)),
            turn: local.turn + own * 30,
          },
          look.size,
          look.tone,
        ),
      );
    });
  }

  const holder = onBoard
    ? [...board(), ...inDish]
    : [...dish.back, group(inDish, { clip: `${dish.opening}${dish.body}` }), ...dish.front];
  const dishNode = faded(visible, [
    group(compact(holder), { transform: { x: at.x, y: at.y, rotate: tilt, ox: 0, oy: 0 } }),
  ]);

  return {
    inside: [...falls, landed.length > 0 ? faded(1 - reset, landed) : null],
    above: [dishNode],
  };
}

/** Vorlage „Zutat hinzugeben“. */
export const mixAddTemplate = defineTemplate({
  id: 'mix.add',
  group: 'mix',
  title: 'Zutat hinzugeben',
  description:
    'Eine Zutat kommt in die Schüssel, den Topf oder die Pfanne: Ein Ei wird am Rand aufgeschlagen, Butter, Zucker oder Stücke kommen aus dem Schälchen, Geschnittenes rutscht vom Brett.',
  durationSeconds: 5,
  keywords: [
    'zugeben',
    'hinzugeben',
    'dazugeben',
    'hinzufügen',
    'zufügen',
    'hineingeben',
    'in die schüssel geben',
    'in den topf geben',
    'in die pfanne geben',
    'zutaten',
    'vom brett',
    'vom schneidebrett',
    'einzeln',
    'ei aufschlagen',
    'eier aufschlagen',
    'in flöckchen',
    'in stücken',
  ],
  stillTime: 0.44,
  parameters: [choice('item', 'Zutat', ITEMS, 'egg'), choice('vessel', 'Gefäß', TARGET_VESSELS, 'bowl')],
  inferValues(text) {
    const lower = text.toLowerCase();
    let item: Item | undefined;
    if (/\bei\b|\beier\b|eigelb/.test(lower)) item = 'egg';
    else if (lower.includes('butter')) item = 'butter';
    else if (/zucker|salz|mehl/.test(lower)) item = 'sugar';
    else if (/brett|geschnitten|gehackt|gewürfelt/.test(lower)) item = 'board';
    else if (/stücke|würfel|gemüse|zwiebel|obst/.test(lower)) item = 'pieces';
    return { item, ...targetFromText(text) };
  },
  scene(values, t) {
    const kind = textValue(values, 'vessel', 'bowl') as TargetVessel;
    const item = textValue(values, 'item', 'egg') as Item;
    const vessel = targetVessel(kind);
    const surfaceY = vessel.rimY + vessel.depth * LEVEL;
    const parts = item === 'egg' ? eggScene(vessel, surfaceY, t) : dishScene(item, vessel, surfaceY, t);
    return targetScene(
      kind,
      vessel,
      t,
      [contents(vessel, LEVEL, 'shade'), group(parts.inside, { clip: interiorClip(vessel, 90) })],
      parts.above,
    );
  },
  hints(values) {
    const kind = textValue(values, 'vessel', 'bowl') as TargetVessel;
    const vessel = targetVessel(kind);
    return [
      arrow([
        { x: vessel.cx + 46, y: vessel.rimY - 40 },
        { x: vessel.cx + 30, y: vessel.rimY - 30 },
        { x: vessel.cx + 10, y: vessel.rimY - 4 },
      ]),
    ];
  },
});
